/* eslint-disable max-len */
import { useCallback } from 'react';
import {
  PageTitle, Upload, BottomButtons, ErrorBar,
} from '@components';
import {
  ClaimType, ALLOWED_UPLOAD_TYPE,
} from '@constants';
import { Snackbar, Alert } from '@mui/material';
import classes from './index.module.css';
import { useUpload } from './hooks';

const TITLE_TEXT = 'Please provide details';
// const UPLOAD_TIP = `Maximum upload file size: ${MAX_UPLOAD_FILE_SIZE}MB, ${MAX_UPLOAD_FILE_NUM} files at most.`;

const UploadText = {
  [ClaimType.Theft]: '1. Upload supporting documents like Police Report/Security Footage/Carrier Letter/Written Documentation from an authority detailing the incident.',
  [ClaimType.Damage]: '1. To qualify for a payout for damaged item(s), please upload at least: 1) a photo of carrier\'s packaging; 2) a photo of product packaging; 3) a photo clearly showing the damage.',
  [ClaimType.Loss]: "1. Please upload a screenshot of the corresponding order page. If the shipment has shown “delivery” scan, but you didn't receive your package, you need to additionally provide one of the following: Police Report/ Security Footage/ Carrier Letter/ Written Documentation from an authority detailing the incident. Learn more about ",
};
const uploadInfoMap = {
  [ClaimType.Damage]: [{
    title: '1. Please upload clear photo(s) of the carrier packaging and product package.',
    helpText: 'Please upload at least 1 file, maximum 10 files. Zip your files if you have more than 10. Maximum file size: 20MB.',
    isRequired: true,
    moreLink: 'https://kover2618.zendesk.com/hc/en-us/articles/15392457332123-What-Supporting-Docs-are-Required-for-a-Claim-of-Damage-',
    moreText: 'supporting docs required for your damage claim',
  }, {
    title: '2. Please upload clear photo(s) of how the item(s) are damaged.',
    helpText: 'Please upload at least 1 file, maximum 10 files. Zip your files if you have more than 10. Maximum file size: 20MB.',
    isRequired: true,
  }],
};
const helpDocLink = {
  Title: 'https://kover2618.zendesk.com/hc/en-us/sections/15653294528155-Shipping-Protection',
  [ClaimType.Damage]: 'https://kover2618.zendesk.com/hc/en-us/articles/15392457332123-What-Supporting-Docs-are-Required-for-a-Claim-of-Damage-',
  [ClaimType.Loss]: 'https://kover2618.zendesk.com/hc/en-us/articles/15392585272603-What-Supporting-Docs-are-Required-for-a-Claim-of-Loss-',
  [ClaimType.Theft]: 'https://kover2618.zendesk.com/hc/en-us/articles/15392190225819-What-Supporting-Docs-are-Required-for-a-Claim-of-Theft-',
};
const commonTipText = 'Please upload at least 1 file, maximum 10 files. Zip your files if you have more than 10. Maximum file size: 20MB.';
const MAX_UPLOAD_AMOUNT_LIMIT = 300;

export const UploadPage = () => {
  const {
    type,
    files, files1,
    inputValue,
    isLoading,
    showToast,
    toastText,
    showFailedToast,
    failedToastText,
    comments,
    totalMoney,
    onCommentsChange,
    onCloseFailedToast,
    handleChange,
    deleteFile,
    onClickContinue,
    onClickBack,
    onCloseToast,
  } = useUpload();

  const commentPart = useCallback((number) => (
    <>
      <div className={classes.header_tip}>
        {`${number}. Enter comments explaining the issue.`}
      </div>
      <textarea
        rows={3}
        className={classes.input_comments}
        onChange={(v) => onCommentsChange(v)}
        value={comments || ''}
        placeholder=" Max 1,000 characters"
        maxLength="1000"
      >
      </textarea>
    </>
  ));

  const uploadContent = (type) => {
    const uploadList = uploadInfoMap[type];
    const len = uploadList.length;

    return uploadList.map((item, index) => {
      const {
        title, helpText, isRequired, moreLink, moreText,
      } = item;

      return (
        <div key={title}>
          <div className={classes.header_tip}>
            {isRequired && <span className={classes.isRequired}>*</span>}
            {title}
            {moreLink ? (
              <span>
                {' Learn more about '}
                <a className={classes.link} href={moreLink} target="_blank" rel="noreferrer">{moreText}</a>
                .
              </span>
            ) : null}
          </div>
          <Upload
            files={index ? files1 : files}
            accept={ALLOWED_UPLOAD_TYPE}
            inputValue={inputValue}
            handleChange={(e) => handleChange(e, index)}
            deleteFile={(v) => deleteFile(v, index)}
            key={title}
            style={{ boxShadow: 'initial', marginTop: 0 }}
          >
            <span>
              {helpText}
            </span>
          </Upload>
          {index === len - 1 && commentPart(len + 1)}
        </div>

      );
    });
  };
  const isDisabled = (type === ClaimType.Loss ? (totalMoney >= MAX_UPLOAD_AMOUNT_LIMIT
    && !files.length) : !files.length) || (type === ClaimType.Damage ? !files1.length : false);

  return (
    <section className={classes.content}>
      <PageTitle title={TITLE_TEXT} />
      <section className={classes.header_tip}>
        {'Thorough details help us understand the problem and process your claim faster. Learn more about '}
        <a href={helpDocLink.Title} className={classes.link} target="_blank" rel="noreferrer">the supporting documents</a>
        .
      </section>
      {/* 提示 toast */}
      {showToast ? (
        <ErrorBar className={classes.error} message={toastText} canClose onClose={onCloseToast} />
      ) : null}
      <div className={classes.card}>

        {type === ClaimType.Damage ? uploadContent(type)
          : (
            <div>
              <div>
                <div className={classes.header_tip}>
                  {UploadText[type]}
                  <a href={helpDocLink[type]} className={classes.link} target="_blank" rel="noreferrer">{`supporting docs required for your ${type} claim`}</a>
                  .
                </div>

                <Upload
                  files={files}
                  accept={ALLOWED_UPLOAD_TYPE}
                  inputValue={inputValue}
                  handleChange={handleChange}
                  deleteFile={deleteFile}
                  style={{ boxShadow: 'initial', marginTop: 0 }}
                >
                  <span>{commonTipText}</span>
                  {/* <span className={classes.upload_tip}>{UPLOAD_TIP}</span> */}
                </Upload>
              </div>
              {commentPart(2)}
            </div>
          )}
      </div>
      <BottomButtons
        isContinueDisabled={isDisabled}
        isContinueLoading={isLoading}
        onClickContinue={onClickContinue}
        onClickBack={onClickBack}
      />

      {showFailedToast ? (
        <Snackbar
          className={classes.bottom_toast}
          open={showFailedToast}
          anchorOrigin={{
            horizontal: 'center',
            vertical: 'bottom',
          }}
          autoHideDuration={3000}
          onClose={onCloseFailedToast}
        >
          <Alert
            icon={false}
            severity="error"
            className={classes.failed_toast}
            onClose={onCloseFailedToast}
          >
            {failedToastText}
          </Alert>
        </Snackbar>
      ) : null}
    </section>
  );
};
